import { useEffect, useState } from "react";
import { Sparkles, X } from "lucide-react";
import { useLang } from "../contexts/LangContext";

/**
 * Subtle, randomized social-proof toast that appears every ~20 seconds.
 * Pulls a random recent-activity message + city + minutes-ago from i18n.
 * Dismissable; resumes after user dismiss for the session.
 */
const CITIES = ["Hamburg", "Bremen", "Hannover", "Lübeck", "Kiel"];

function pick(arr) { return arr[Math.floor(Math.random() * arr.length)]; }

export default function LiveActivity() {
  const { t } = useLang();
  const [open, setOpen] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const [msg, setMsg] = useState(null);

  useEffect(() => {
    if (dismissed) return;
    let mounted = true;
    let timer;

    const buildMessage = () => {
      const activities = t.live_activity?.activities;
      if (!activities || !Array.isArray(activities) || activities.length === 0) return null;
      const activity = pick(activities);
      const city = pick(CITIES);
      const mins = Math.floor(Math.random() * 18) + 2;
      return { activity, city, mins };
    };

    const show = () => {
      if (!mounted) return;
      const m = buildMessage();
      if (!m) return;
      setMsg(m);
      setOpen(true);
      timer = setTimeout(() => {
        if (!mounted) return;
        setOpen(false);
        timer = setTimeout(show, 18000); // next reveal
      }, 6500);
    };

    // first reveal after 9 seconds (let user start scrolling)
    timer = setTimeout(show, 9000);

    return () => { mounted = false; clearTimeout(timer); };
  }, [dismissed, t.live_activity]);

  if (dismissed || !open || !msg) return null;
  const tmpl = t.live_activity.template || "{activity} · {city} · {mins} {minutes_ago}";
  const text = tmpl
    .replace("{activity}", msg.activity)
    .replace("{city}", msg.city)
    .replace("{mins}", msg.mins);

  return (
    <div
      className="fixed bottom-24 start-5 z-50 max-w-[320px] bg-white border border-neutral-200 shadow-2xl rounded-sm p-4 flex items-start gap-3 animate-[reveal_.5s_ease-out]"
      data-testid="live-activity-toast"
      role="status"
    >
      <div className="shrink-0 w-9 h-9 bg-[#FF5A00]/10 text-[#FF5A00] flex items-center justify-center rounded-sm">
        <Sparkles size={18} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-[10px] font-bold tracking-[0.22em] uppercase text-[#FF5A00] mb-1">{t.live_activity.label}</div>
        <p className="text-sm font-semibold text-neutral-900 leading-snug">{text}</p>
      </div>
      <button
        type="button"
        onClick={() => setDismissed(true)}
        aria-label="Close"
        className="shrink-0 text-neutral-400 hover:text-neutral-900 transition-colors"
        data-testid="live-activity-close"
      >
        <X size={16} />
      </button>
    </div>
  );
}
